import type {
  CmmdBoardTask,
  CmmdProject,
  EnvironmentId,
  ModelSelection,
  ProviderInteractionMode,
  ProviderKind,
  RuntimeMode,
  ThreadId,
} from "@cmmd-forge/contracts";
import { useMemo, useRef, useState } from "react";

import {
  providerPolicyForAccess,
  useForgeProviderAccess,
  type ProviderCredentialMode,
} from "~/cmmd/forgeProviderAccess";
import { useShellProviderAvailability } from "~/cmmd/useShellProviderAvailability";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { usePrimaryEnvironmentId } from "~/environments/primary";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { useSettings } from "~/hooks/useSettings";
import { newCommandId } from "~/lib/utils";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { useServerConfig, useServerProviders } from "~/rpc/serverState";
import type { ForgeThreadTarget } from "../projects/forgeExecutionTarget";
import type { ThreadLaunchConfiguration } from "../NoActiveThreadState.threadRunner";
import {
  startOrResumeTaskWorkSession,
  type ExistingTaskConversation,
  type TaskWorkSessionCheckpoint,
} from "./taskWorkSessionLaunch";
import {
  CLOSED_STATE,
  DEFAULT_CONFIGURATION,
  NO_AUTHORIZED_FORGE_WORKSPACE_MESSAGE,
  createCheckpoint,
  createTaskThread,
  flattenTaskWorkTargets,
  isReadyTaskWorkTarget,
  notifySessionStarted,
  prepareTaskThreadLaunch,
  requireReadyLaunchTarget,
  resolveStartContext,
  resolveTaskWorkProviderLaunchReadiness,
  resolveTaskWorkRunnableProviders,
  resolveTaskWorkSessionCredentialMode,
  type LauncherState,
  type PreparedTaskThreadLaunch,
} from "./useTaskWorkSessionLauncher.logic";

const api = createCmmdApiClient();
const EMPTY_TARGET_PROVIDERS = [] as const;

export interface UseTaskWorkSessionLauncherInput {
  readonly project: CmmdProject | null;
  readonly workspaceName: string | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly resolveExistingConversation: (
    taskId: number,
  ) => Promise<ExistingTaskConversation | null>;
  readonly onOpenThread: (target: ForgeThreadTarget) => void;
  readonly onSessionStarted: () => void;
}

export function useTaskWorkSessionLauncher(input: UseTaskWorkSessionLauncherInput) {
  const [state, setState] = useState<LauncherState>(CLOSED_STATE);
  const checkpointRef = useRef<TaskWorkSessionCheckpoint | null>(null);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const defaultThreadRuntime = useSettings((settings) => settings.defaultThreadRuntime);
  const defaultThreadEnvMode = useSettings((settings) => settings.defaultThreadEnvMode);
  const primaryServerConfig = useServerConfig();
  const serverProviders = useServerProviders();
  const targets = useMemo(() => flattenTaskWorkTargets(state.options), [state.options]);
  const selectedTarget = targets.find((target) => target.key === state.selectedTargetKey) ?? null;
  const savedTargetProviders = useSavedEnvironmentRuntimeStore((runtimeState) =>
    selectedTarget === null || selectedTarget.environmentId === primaryEnvironmentId
      ? null
      : (runtimeState.byId[selectedTarget.environmentId as EnvironmentId]?.serverConfig
          ?.providers ?? null),
  );
  const providerCredentialMode: ProviderCredentialMode =
    resolveTaskWorkSessionCredentialMode(selectedTarget);
  const providerAccess = useForgeProviderAccess({
    enabled: selectedTarget !== null,
    credentialMode: providerCredentialMode,
    runtimeId: selectedTarget?.runtimeId ?? null,
  });
  const targetProviders =
    selectedTarget?.environmentId === primaryEnvironmentId
      ? serverProviders
      : (savedTargetProviders ?? EMPTY_TARGET_PROVIDERS);
  const targetProviderInventoryKnown =
    selectedTarget !== null &&
    (selectedTarget.environmentId === primaryEnvironmentId
      ? primaryServerConfig !== null
      : savedTargetProviders !== null);
  const runnableTargetProviders = useMemo(
    () =>
      resolveTaskWorkRunnableProviders({
        providers: targetProviders,
        access: providerAccess,
        credentialMode: providerCredentialMode,
      }),
    [providerAccess, providerCredentialMode, targetProviders],
  );
  /**
   * Why the picker may withhold each provider it shows, for the selected
   * launch target. The picker gets the target's Provider Inventory
   * unfiltered (`targetProviders`), not `runnableTargetProviders`: that list
   * still gates launch readiness below, which is a different question from
   * what the picker should show.
   */
  const providerAvailability = useShellProviderAvailability({
    providers: targetProviders,
    policy: providerPolicyForAccess(providerAccess),
    access: providerAccess,
    credentialMode: providerCredentialMode,
    hasLiveInventory: targetProviderInventoryKnown,
  });
  const providerLaunchReadiness =
    selectedTarget !== null && isReadyTaskWorkTarget(selectedTarget)
      ? resolveTaskWorkProviderLaunchReadiness({
          providers: targetProviders,
          runnableProviders: runnableTargetProviders,
          inventoryKnown: targetProviderInventoryKnown,
          access: providerAccess,
        })
      : { canStart: false, unavailableReason: null };
  const initialConfiguration = (): ThreadLaunchConfiguration => ({
    ...DEFAULT_CONFIGURATION,
    environmentMode: defaultThreadEnvMode === "local" ? "local" : "worktree",
    modelSelection: null,
  });

  const open = async (task: CmmdBoardTask) => {
    checkpointRef.current = null;
    setState({
      task,
      options: [],
      selectedTargetKey: null,
      loading: true,
      starting: false,
      error: null,
      existingConversation: null,
      configuration: initialConfiguration(),
    });
    try {
      const existingConversation = await input.resolveExistingConversation(task.id);
      if (existingConversation !== null) {
        setState({
          task,
          options: [],
          selectedTargetKey: null,
          loading: false,
          starting: false,
          error: null,
          existingConversation,
          configuration: initialConfiguration(),
        });
        return;
      }
      const options = await api.listTaskExecutionOptions(String(task.projectId), task.id);
      const nextTargets = flattenTaskWorkTargets(options);
      const preferred = nextTargets.find(isReadyTaskWorkTarget) ?? null;
      setState({
        task,
        options,
        selectedTargetKey: preferred?.key ?? null,
        loading: false,
        starting: false,
        error: nextTargets.length === 0 ? NO_AUTHORIZED_FORGE_WORKSPACE_MESSAGE : null,
        existingConversation: null,
        configuration: initialConfiguration(),
      });
      if (preferred) checkpointRef.current = createCheckpoint();
    } catch (cause) {
      setState({
        task,
        options: [],
        selectedTargetKey: null,
        loading: false,
        starting: false,
        error: sanitizePublicUiError(cause, "Could not load available runtimes."),
        existingConversation: null,
        configuration: initialConfiguration(),
      });
    }
  };

  const close = () => {
    if (state.starting) return;
    checkpointRef.current = null;
    setState(CLOSED_STATE);
  };

  const selectTarget = (targetKey: string) => {
    checkpointRef.current = createCheckpoint();
    setState((current) => ({
      ...current,
      selectedTargetKey: targetKey,
      error: null,
    }));
  };

  const setModelSelection = (provider: ProviderKind, model: string) => {
    setState((current) => ({
      ...current,
      configuration: {
        ...current.configuration,
        modelSelection: { provider, model } as ModelSelection,
      },
    }));
  };

  const setEnvironmentMode = (environmentMode: "local" | "worktree") => {
    setState((current) => ({
      ...current,
      configuration: { ...current.configuration, environmentMode },
    }));
  };

  const setBaseBranch = (baseBranch: string) => {
    setState((current) => ({
      ...current,
      configuration: { ...current.configuration, baseBranch },
    }));
  };

  const setInteractionMode = (interactionMode: ProviderInteractionMode) => {
    setState((current) => ({
      ...current,
      configuration: { ...current.configuration, interactionMode },
    }));
  };

  const setRuntimeMode = (runtimeMode: RuntimeMode) => {
    setState((current) => ({
      ...current,
      configuration: { ...current.configuration, runtimeMode },
    }));
  };

  const start = async () => {
    const launchContext = resolveStartContext(
      state,
      selectedTarget,
      input.project,
      providerLaunchReadiness.canStart,
    );
    if (launchContext === null) return;
    const { task, project, launchTarget } = launchContext;
    const checkpoint = checkpointRef.current ?? createCheckpoint();
    checkpointRef.current = checkpoint;
    setState((current) => ({ ...current, starting: true, error: null }));
    let preparedLaunch: PreparedTaskThreadLaunch | null = null;
    try {
      const result = await startOrResumeTaskWorkSession({
        api,
        projectId: String(task.projectId),
        taskId: task.id,
        taskTitle: task.title,
        target: launchTarget,
        checkpoint,
        resolveExistingConversation: () => input.resolveExistingConversation(task.id),
        createIdempotencyKey: newCommandId,
        onCheckpoint: (next) => {
          checkpointRef.current = next;
        },
        preflight: async () => {
          preparedLaunch = await prepareTaskThreadLaunch({
            launchTarget: requireReadyLaunchTarget(launchTarget),
            project,
            primaryEnvironmentId,
            primaryProviders: serverProviders,
            preferredModelSelection: state.configuration.modelSelection,
          });
        },
        createThread: async ({ threadId }) => {
          if (preparedLaunch === null) {
            throw new Error("Forge did not complete provider access checks for this task.");
          }
          await createTaskThread({
            task,
            project,
            workspaceName: input.workspaceName,
            allTasks: input.tasks,
            environmentId: preparedLaunch.projectRef.environmentId,
            projectId: preparedLaunch.projectRef.projectId,
            threadId: threadId as ThreadId,
            projects: preparedLaunch.projects,
            primaryEnvironmentId,
            defaultThreadRuntime,
            serverProviders,
            launchConfiguration: state.configuration,
            preparedProviderLaunch: preparedLaunch.providerLaunch,
          });
        },
      });
      notifySessionStarted(result, input.onSessionStarted);
      setState(CLOSED_STATE);
      checkpointRef.current = null;
      input.onOpenThread({
        environmentId: result.environmentId,
        threadId: result.threadId,
      });
    } catch (cause) {
      setState((current) => ({
        ...current,
        starting: false,
        error: sanitizePublicUiError(cause, "Could not start this task here."),
      }));
    }
  };

  return {
    open: state.task !== null,
    task: state.task,
    options: state.options,
    targets,
    selectedTargetKey: state.selectedTargetKey,
    loading: state.loading,
    starting: state.starting,
    error: state.error,
    existingConversation: state.existingConversation,
    canStartNewConversation: providerLaunchReadiness.canStart,
    startUnavailableReason: providerLaunchReadiness.unavailableReason,
    configuration: state.configuration,
    // The picker's own input: the target's Provider Inventory as published,
    // not the eligible subset. `providerAvailability` is what tells the
    // picker which of these a person may not select, and why.
    serverProviders: targetProviders,
    providerAvailability,
    orgProviderPolicy: providerPolicyForAccess(providerAccess),
    selectTarget,
    setModelSelection,
    setEnvironmentMode,
    setBaseBranch,
    setInteractionMode,
    setRuntimeMode,
    start,
    close,
    openForTask: open,
  };
}

export type TaskWorkSessionLauncher = ReturnType<typeof useTaskWorkSessionLauncher>;
